import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion } from 'react-bootstrap';
import {
  AppCard,
  AppRemixIcon,
  AppModal,
  AppInput,
  AppTextArea,
  AppMultiSelect,
  AppCheckbox,
  AppNoInfo,
} from '@/layout/AppComponents';
import axios from 'axios';
import styled from 'styled-components';
import Calendar from 'rc-year-calendar';
import 'rc-year-calendar/locales/rc-year-calendar.cs';
import 'rc-year-calendar/locales/rc-year-calendar.de';
import 'rc-year-calendar/locales/rc-year-calendar.es';
import 'rc-year-calendar/locales/rc-year-calendar.fi';
import 'rc-year-calendar/locales/rc-year-calendar.fr';
import 'rc-year-calendar/locales/rc-year-calendar.hr';
import 'rc-year-calendar/locales/rc-year-calendar.hu';
import 'rc-year-calendar/locales/rc-year-calendar.it';
import 'rc-year-calendar/locales/rc-year-calendar.lv';
import 'rc-year-calendar/locales/rc-year-calendar.pt';
import 'rc-year-calendar/locales/rc-year-calendar.pl';
import 'rc-year-calendar/locales/rc-year-calendar.ru';
import 'rc-year-calendar/locales/rc-year-calendar.sk';
import ReactDOMServer from 'react-dom/server';
import Sidebar from '../../../layout/Sidebar/Sidebar';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import AppLink from '../../../routing/helpers/AppLink';
import { axiosPath, path } from '../../../routing/helpers/router';
import Translator from '../../../layout/components/helpers/Translator';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';

export default function GlobalLegalComplianceCalendarScreen() {
  const { clientID, moduleBridgeHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSidebarOpened = useSelector(
    (state) => state?.auth?.settings?.layout?.isSidebarOpened,
  );
  const global = useSelector((state) => state.global);
  const client = useSelector((state) => state.auth.user.client);
  const [calendarData, setCalendarData] = useState();
  const [selectedDateModal, setSelectedDateModal] = useState();
  const [plantHexs, setPlantHexs] = useState([]);
  const [userPlants, setUserPlants] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [activateFilter, setActivateFilter] = useState();
  const [checkingObligation, setCheckingObligation] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingXls, setIsDownloadingXls] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // > LOAD CALENDAR INFO
  const loadCalendarInfo = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        axiosPath(
          'api_r_user_module_globalLegalCompliance_yearCalendar_getInfo',
        ),
        { moduleBridgeHex, plantHexs, showResolved },
      );
      if (isMounted.current) {
        setCalendarData(
          response.data.calendarDates.map((cd) => ({
            ...cd,
            startDate: new Date(cd.date.Y, cd.date.m, cd.date.d),
            endDate: new Date(cd.date.Y, cd.date.m, cd.date.d),
          })),
        );
        setUserPlants(response.data.userPlants);
      }
      setIsLoading(false);
    } catch (err) {}
  }, [showResolved, plantHexs]);

  useEffect(() => {
    loadCalendarInfo();
  }, []);

  useEffect(() => {
    loadCalendarInfo();
  }, [activateFilter]);
  // < LOAD CALENDAR INFO

  // > HANDLE CALENDAR
  const calendarController = (e) => {
    if (e.events.length > 0) setSelectedDateModal(e.events);
  };

  const handleCloseModal = () => {
    setSelectedDateModal(undefined);
  };
  // > HANDLE CALENDAR

  const checkObligation = async (
    obligationID,
    legalComplianceCalendarDateID,
  ) => {
    if (checkingObligation) return;
    setCheckingObligation(legalComplianceCalendarDateID);
    try {
      const response = await axios.post(
        axiosPath(
          'api_r_user_module_globalLegalCompliance_yearCalendar_generateFilterHexCode',
        ),
        {
          moduleBridgeHex,
          legalComplianceCalendarDateID,
          filters: { id: obligationID },
        },
      );
      const url = path({
        name: 'user_module_legalCompliance_evaluation',
        params: {
          moduleBridgeHex: response.data.moduleHex,
          plantHex: response.data.plantHex,
          page: 1,
          filtersHex: response.data.hexCode,
        },
      });
      window.open(url, '_blank', 'noopener,noreferrer');

      setCheckingObligation(undefined);
    } catch (err) {}
  };

  // > DOWNLOAD XLS
  const handleDownloadXls = async () => {
    if (isDownloadingXls) return;
    setIsDownloadingXls(true);
    try {
      const response = await axios.post(
        axiosPath(
          'api_r_user_module_globalLegalCompliance_yearCalendar_downloadXls',
        ),
        {
          moduleBridgeHex,
          plantHexs,
          showResolved,
          year: currentYear,
        },
      );
      window.location = response.data.s3FileUrl;
      setIsDownloadingXls(false);
    } catch (err) {
      dispatch(errorHandler(err));
      return setIsDownloadingXls(false);
    }
  };
  // < DOWNLOAD XLS

  return (
    <section className="container-fluid">
      <BreadcrumbHandler
        paths={[
          {
            name: ReactDOMServer.renderToString(
              <Translator file="user" name="user.calendar" />,
            ),
          },
        ]}
      />
      <Sidebar>
        <img
          src={client?.logoUrl}
          className="img-fluid"
          alt={client?.name}
          title={client?.name}
        />
        <Accordion>
          <ul>
            <li>
              <AppLink
                path="user_module_globalLegalCompliance_evaluation"
                params={{
                  moduleBridgeHex,
                  page: 1,
                  filtersHex: 0,
                }}
                className="sidebar-element"
              >
                <AppRemixIcon
                  icon="arrow-go-back-line"
                  className="sidebar-icon mr-1"
                  noVariants
                />
                <span className="sb-title">
                  <Translator file="module-legalCompliance" name="go.back" />
                </span>
              </AppLink>
            </li>
          </ul>
        </Accordion>
      </Sidebar>
      <div className="row">
        <main role="main" className="col-md-12 col-lg-12 px-md-4">
          <div
            className={`main-content ${isSidebarOpened && 'sidebar-expanded'}`}
          >
            <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-0 mb-0 border-bottom">
              <section className="content">
                <AppCard
                  header={
                    <span
                      className={`btn btn-success btn-xs float-right${
                        isDownloadingXls ? ' disabled' : ''
                      }`}
                      onClick={() =>
                        isDownloadingXls ? undefined : handleDownloadXls()
                      }
                    >
                      <AppRemixIcon
                        icon={isDownloadingXls ? 'loader-4' : 'file-excel'}
                      />
                    </span>
                  }
                >
                  <div className="row">
                    <div className="col-lg-5">
                      <AppCheckbox
                        value={showResolved}
                        onChange={() => setShowResolved(!showResolved)}
                        hasBorder
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="show.resolved.events"
                        />
                      </AppCheckbox>
                    </div>
                    <div className="col-lg-6">
                      <AppMultiSelect
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="filter.plants"
                          />,
                        )}
                        placeholder={ReactDOMServer.renderToString(
                          <Translator
                            file="module-globallegalCompliance"
                            name="show.all"
                          />,
                        )}
                        options={userPlants || []}
                        isLoading={!userPlants}
                        value={plantHexs}
                        onChange={(v) => setPlantHexs(v)}
                        allowSelectAll
                        hasSearchBox
                        hasSelectOptions
                      />
                    </div>
                    <div className="col-lg-1">
                      <span
                        className="btn btn-info btn-block mt-2"
                        onClick={() => setActivateFilter(Math.random())}
                      >
                        <Translator
                          file="module-globallegalCompliance"
                          name="search"
                        />
                      </span>
                    </div>
                  </div>

                  {!isLoading && calendarData ? (
                    <Calendar
                      language={global?.urlData?.lang}
                      dataSource={calendarData}
                      style="background"
                      onDayClick={calendarController}
                      onYearChanged={(e) => setCurrentYear(e.currentYear)}
                    />
                  ) : (
                    <AppNoInfo
                      text={`${ReactDOMServer.renderToString(
                        <Translator file="general" name="loading" />,
                      )}...`}
                    />
                  )}
                </AppCard>
              </section>
            </div>
          </div>
        </main>
      </div>
      <AppModal
        show={!!selectedDateModal}
        size="lg"
        isConfirmBtn
        confirmBtnText={ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="close" />,
        )}
        title={(selectedDateModal && selectedDateModal[0]?.dateFormatted) || ''}
        onConfirm={handleCloseModal}
        onClose={handleCloseModal}
      >
        <ModalWrapper>
          {selectedDateModal &&
            selectedDateModal.map((sd) => (
              <SelectedDateContainer key={sd.id} className="mb-2">
                <div className="container-fluid">
                  {sd.hex && (
                    <div className="row">
                      <div className="col-lg-12 text-right id">
                        CODE: <b>{sd.hex}</b>
                      </div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col-md-12">
                      <AppInput
                        icon={<AppRemixIcon icon="calendar" />}
                        title={
                          <Translator file="module-intelligence" name="plant" />
                        }
                        value={
                          <>
                            <AppLink
                              path="user_module_legalCompliance_home"
                              params={{
                                moduleBridgeHex: sd.plant?.moduleBridgeHex,
                              }}
                              className="sidebar-element"
                              target="_blank"
                            >
                              {sd.plant?.moduleName}
                            </AppLink>
                            <span className="ml-1 mr-1">/</span>
                            <AppLink
                              path="user_module_legalCompliance_evaluation"
                              params={{
                                moduleBridgeHex: sd.plant?.moduleBridgeHex,
                                plantHex: sd.plant?.hex,
                                page: 1,
                                filtersHex: 0,
                              }}
                              className="sidebar-element"
                              target="_blank"
                            >
                              {sd.plant?.name}
                            </AppLink>
                          </>
                        }
                        isReadOnly
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="calendar" />}
                        title={
                          <Translator
                            file="module-legalCompliance"
                            name="date"
                          />
                        }
                        value={sd.dateFormatted}
                        isReadOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        icon={<AppRemixIcon icon="apps-2" />}
                        title={<Translator file="user" name="user.state" />}
                        value={sd.stateName}
                        isReadOnly
                      />
                    </div>
                  </div>
                  {sd.description && (
                    <div className="row">
                      <div className="col-lg-12">
                        <AppTextArea
                          title={
                            <Translator
                              file="module-legalCompliance"
                              name="comment"
                            />
                          }
                          value={sd.description}
                          isDisabled
                        />
                      </div>
                    </div>
                  )}
                  {sd.evidences.length > 0 && (
                    <>
                      <h6>
                        <Translator
                          file="module-legalCompliance"
                          name="evidences"
                        />
                      </h6>
                      {sd.evidences.map((e) => (
                        <React.Fragment key={e.id}>
                          <div className="row">
                            <div className="col-md-12 col-lg-4">
                              <AppInput
                                icon={
                                  <AppRemixIcon
                                    icon="double-quotes-l"
                                    noVariants
                                  />
                                }
                                title={
                                  <Translator
                                    file="module-legalCompliance"
                                    name="name"
                                  />
                                }
                                value={e.name}
                                isReadOnly
                              />
                            </div>
                            <div className="col-md-12 col-lg-4">
                              <AppInput
                                icon={<AppRemixIcon icon="calendar" />}
                                title={
                                  <Translator
                                    file="module-legalCompliance"
                                    name="date"
                                  />
                                }
                                value={e.date}
                                isReadOnly
                              />
                            </div>
                            <div className="col-md-12 col-lg-4">
                              <AppInput
                                icon={<AppRemixIcon icon="file" />}
                                title={
                                  <Translator
                                    file="module-legalCompliance"
                                    name="associated.files"
                                  />
                                }
                                value={e.urls}
                                isReadOnly
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-lg-12">
                              <AppTextArea
                                title={
                                  <Translator
                                    file="module-legalCompliance"
                                    name="comment"
                                  />
                                }
                                value={e.description}
                                isDisabled
                              />
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                  <div className="row">
                    <div className="col-lg-12">{sd.name}</div>
                    <div className="col-lg-12">
                      <AppTextArea
                        title={
                          <Translator file="user" name="user.obligation" />
                        }
                        value={
                          sd?.hasFormat ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: sd.obligation,
                              }}
                            />
                          ) : (
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {sd.obligation}
                            </div>
                          )
                        }
                        isReadOnly
                      />
                    </div>
                    <div className="col-lg-12">
                      <span
                        className={`btn btn-xs btn-${
                          sd.resolved ? 'success' : 'danger'
                        }${checkingObligation === sd.id ? ' disabled' : ''}`}
                        onClick={() => checkObligation(sd.obligationId, sd.id)}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="see.obligation"
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </SelectedDateContainer>
            ))}
        </ModalWrapper>
      </AppModal>
    </section>
  );
}

const ModalWrapper = styled.div`
  max-height: 304px;
  overflow-y: auto;
`;

const SelectedDateContainer = styled.div`
  border: 1px solid #ddd;
  padding: 20px;
  .id {
    font-size: 10px;
  }
`;
